import type { IsoInstant, MoneyCzk } from '@swi/contracts';

const czk = new Intl.NumberFormat('cs-CZ', {
  style: 'currency',
  currency: 'CZK',
  maximumFractionDigits: 0,
});

const czkExact = new Intl.NumberFormat('cs-CZ', {
  style: 'currency',
  currency: 'CZK',
  minimumFractionDigits: 2,
});

/** Money arrives as a fixed-point string; only formatting turns it into a number. */
export const money = (value: MoneyCzk | string): string => czk.format(Number(value));
export const moneyExact = (value: MoneyCzk | string): string => czkExact.format(Number(value));

const czkCompact = new Intl.NumberFormat('cs-CZ', {
  style: 'currency',
  currency: 'CZK',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Shortened amount for KPI tiles ("1,9 mil. Kč").
 *
 * A month of charter revenue runs to eight digits, which overflows a tile; the
 * exact figure stays available in the tile's title attribute.
 */
export const moneyCompact = (value: MoneyCzk | string): string => {
  const amount = Number(value);
  return Math.abs(amount) >= 100_000 ? czkCompact.format(amount) : czk.format(amount);
};

export const number = (value: number, digits = 0): string =>
  new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);

/**
 * Instants are UTC on the wire; converting to a local zone happens here and
 * nowhere else.
 */
export const dateTime = (value: IsoInstant | string, timeZone?: string): string =>
  new Intl.DateTimeFormat('cs-CZ', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(new Date(value));

export const dateOnly = (value: IsoInstant | string, timeZone?: string): string =>
  new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeZone }).format(new Date(value));

export const timeOnly = (value: IsoInstant | string, timeZone?: string): string =>
  new Intl.DateTimeFormat('cs-CZ', { timeStyle: 'short', timeZone }).format(new Date(value));

export const weekday = (value: IsoInstant | string, timeZone?: string): string =>
  new Intl.DateTimeFormat('cs-CZ', { weekday: 'short', timeZone }).format(new Date(value));

export function duration(fromIso: string, toIso: string): string {
  const minutes = Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60_000);
  return durationMinutes(minutes);
}

export function durationMinutes(minutes: number): string {
  const sign = minutes < 0 ? '-' : '';
  const absolute = Math.abs(minutes);
  const days = Math.floor(absolute / 1440);
  const hours = Math.floor((absolute % 1440) / 60);
  const mins = absolute % 60;

  if (days > 0) {
    return `${sign}${String(days)} d ${String(hours)} h`;
  }
  if (hours > 0) {
    return `${sign}${String(hours)} h ${String(mins).padStart(2, '0')} min`;
  }
  return `${sign}${String(mins)} min`;
}

export function relative(value: IsoInstant | string): string {
  const diffMinutes = Math.round((new Date(value).getTime() - Date.now()) / 60_000);
  const formatter = new Intl.RelativeTimeFormat('cs-CZ', { numeric: 'auto' });

  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, 'minute');
  }
  if (Math.abs(diffMinutes) < 1440) {
    return formatter.format(Math.round(diffMinutes / 60), 'hour');
  }
  return formatter.format(Math.round(diffMinutes / 1440), 'day');
}

export const coords = (latitude: number, longitude: number): string =>
  `${latitude.toFixed(3)}°, ${longitude.toFixed(3)}°`;

/** Input type="datetime-local" wants local wall-clock text, not an instant. */
export function toLocalInputValue(value: Date | string): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromLocalInputValue(value: string): Date {
  return new Date(value);
}
